/**
 * Helper utilities for working with URL query parameters using React Router's useSearchParams.
 * These replace the use-query-params library with native React Router hooks.
 */

import { useSearchParams } from "react-router-dom";
import { useCallback } from "react";

/**
 * Parses a query parameter as a boolean value.
 * Accepts both 'true' and '1' as truthy for backward compatibility
 * with use-query-params BooleanParam encoding.
 */
export const parseBooleanParam = (value: string | null, defaultValue: boolean): boolean => {
  if (value === null) return defaultValue;
  return value === 'true' || value === '1';
};

/**
 * Parses a query parameter as a number (non-negative).
 */
export const parseNumberParam = (value: string | null, defaultValue: number): number => {
  if (value === null || value === "") return defaultValue;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : defaultValue;
};

/**
 * Parses a query parameter as a string.
 */
export const parseStringParam = (value: string | null, defaultValue: string): string => {
  return value ?? defaultValue;
};

/**
 * Parses a query parameter as an array of strings (comma-separated).
 * Compatible with use-query-params ArrayParam format.
 */
export const parseArrayParam = (value: string | null, defaultValue: string[] = []): string[] => {
  if (value === null || value === "") return defaultValue;
  return value.split(",").filter(v => v !== "");
};

/**
 * Hook for managing a query parameter with automatic type inference.
 * Returns [value, setValue] tuple based on the defaultValue type.
 *
 * @example
 * const [page, setPage] = useQueryParam("page", 0); // number
 * const [query, setQuery] = useQueryParam("query", ""); // string
 * const [active, setActive] = useQueryParam("active", true); // boolean
 * const [tags, setTags] = useQueryParam("tags", []); // string[] | undefined
 */
type QueryParamSetter<T> = (value: T) => void;

export function useQueryParam(
  key: string,
  defaultValue: boolean
): readonly [boolean, QueryParamSetter<boolean>];

export function useQueryParam(
  key: string,
  defaultValue: number
): readonly [number, QueryParamSetter<number>];

export function useQueryParam(
  key: string,
  defaultValue: string
): readonly [string, QueryParamSetter<string>];

export function useQueryParam(
  key: string,
  defaultValue: string[]
): readonly [string[] | undefined, QueryParamSetter<string[] | undefined>];

export function useQueryParam(
  key: string,
  defaultValue: boolean | number | string | string[]
): readonly [any, QueryParamSetter<any>] {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse value based on type
  const value = (() => {
    const rawValue = searchParams.get(key);

    if (typeof defaultValue === "boolean") {
      return parseBooleanParam(rawValue, defaultValue);
    }
    if (typeof defaultValue === "number") {
      return parseNumberParam(rawValue, defaultValue);
    }
    if (Array.isArray(defaultValue)) {
      // Match use-query-params ArrayParam behavior: null param returns undefined
      return rawValue === null ? undefined : parseArrayParam(rawValue, defaultValue);
    }
    // string
    return parseStringParam(rawValue, defaultValue);
  })();

  const setValue = useCallback(
    (newValue: typeof value) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);

        // Handle array type
        if (Array.isArray(defaultValue)) {
          if (!Array.isArray(newValue) || newValue.length === 0) {
            next.delete(key);
          } else {
            next.set(key, newValue.join(","));
          }
        } else {
          // Handle primitive types
          if (newValue === defaultValue) {
            next.delete(key);
          } else {
            next.set(key, String(newValue));
          }
        }

        return next;
      }, { replace: true });
    },
    [setSearchParams, key, defaultValue]
  );

  return [value, setValue] as const;
}

// Legacy exports for backward compatibility (deprecated, use useQueryParam instead)
export const useBooleanQueryParam = (key: string, defaultValue: boolean) =>
  useQueryParam(key, defaultValue);
export const useNumberQueryParam = (key: string, defaultValue: number) =>
  useQueryParam(key, defaultValue);
export const useStringQueryParam = (key: string, defaultValue: string) =>
  useQueryParam(key, defaultValue);
export const useArrayQueryParam = (key: string, defaultValue: string[] = []) =>
  useQueryParam(key, defaultValue);

/**
 * Configuration for a single query parameter in useQueryParams
 */
export interface QueryParamConfig<T> {
  parse: (value: string | null) => T;
  defaultValue: T;
}

/**
 * Configuration object for useQueryParams hook
 */
export type QueryParamsConfig<T extends Record<string, unknown>> = {
  [K in keyof T]: QueryParamConfig<T[K]>;
};

/**
 * Hook for managing multiple query parameters at once.
 * Similar to useQueryParams from use-query-params.
 *
 * @param config - Object mapping param keys to their parsers and defaults
 * @returns [values, setValues] where setValues accepts a partial update or updater function
 *
 * @example
 * const [filters, setFilters] = useQueryParams({
 *   active: { parse: (v) => parseBooleanParam(v, true), defaultValue: true },
 *   search: { parse: (v) => parseStringParam(v, ""), defaultValue: "" }
 * });
 */
export const useQueryParams = <T extends Record<string, unknown>>(
  config: QueryParamsConfig<T>
): readonly [T, (updater: Partial<T> | ((prev: T) => T)) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const configKeys = Object.keys(config) as Array<keyof T>;

  // Parse all values from current searchParams
  const values = configKeys.reduce((acc, key) => {
    const { parse } = config[key];
    acc[key] = parse(searchParams.get(String(key)));
    return acc;
  }, {} as T);

  // Setter function - recreated on each render since config changes
  // This is intentional and fine for this use case
  const setValues = (updater: Partial<T> | ((prev: T) => T)) => {
    setSearchParams((current) => {
      // Parse current values from current searchParams
      const currentValues = configKeys.reduce((acc, key) => {
        const { parse } = config[key];
        acc[key] = parse(current.get(String(key)));
        return acc;
      }, {} as T);

      const newValues = typeof updater === 'function'
        ? updater(currentValues)
        : { ...currentValues, ...updater };

      const next = new URLSearchParams(current);

      // Update all params
      configKeys.forEach((key) => {
        const { defaultValue } = config[key];
        const newValue = newValues[key];

        if (newValue === defaultValue) {
          next.delete(String(key));
        } else {
          next.set(String(key), String(newValue));
        }
      });

      return next;
    }, { replace: true });
  };

  return [values, setValues] as const;
};
